import { Controller } from 'stimulus'

/**
 * SyncStatusController
 * Handles polling for Route53 sync status updates
 */
export default class extends Controller {
  static targets = ['route53Card', 'route53Status']
  static values = {
    url: String,
    polling: Boolean,
    interval: Number,
    maxAttempts: Number
  }

  connect() {
    this.attempts = 0
    this.pollTimer = null
    // Set defaults if not provided (Stimulus 2.x compatibility)
    this._polling = false
    this._interval = this.hasIntervalValue ? this.intervalValue : 3000
    this._maxAttempts = this.hasMaxAttemptsValue ? this.maxAttemptsValue : 40
  }

  disconnect() {
    this.stopPolling()
  }

  // Start polling for sync status
  startPolling() {
    if (this._polling) return

    this._polling = true
    this.attempts = 0
    this.poll()
  }

  // Stop polling
  stopPolling() {
    this._polling = false
    if (this.pollTimer) {
      clearTimeout(this.pollTimer)
      this.pollTimer = null
    }
  }

  // Perform a single poll
  async poll() {
    if (!this._polling) return

    try {
      const response = await fetch(this.urlValue, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin'
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      this.updateStatus(data)

      // Check if sync is complete (no longer in progress)
      if (!data.sync_in_progress) {
        if (data.last_sync_error) {
          this.stopPolling()
          this.showErrorNotification(data.last_sync_error)
          return
        } else if (data.dns_synced && !data.dns_sync_to_do) {
          this.stopPolling()
          this.showSuccessNotification()
          return
        }
      }

      // Continue polling while sync is in progress or not at max attempts
      this.attempts++
      if (data.sync_in_progress || this.attempts < this._maxAttempts) {
        this.pollTimer = setTimeout(() => this.poll(), this._interval)
      } else {
        this.stopPolling()
        this.showTimeoutNotification()
      }
    } catch (error) {
      console.error('Error polling sync status:', error)
      this.stopPolling()
    }
  }

  // Update the UI with new status
  updateStatus(data) {
    if (!this.hasRoute53StatusTarget) return

    let statusHtml = ''
    let cardClass = ''

    // Use the new sync_in_progress field for accurate status
    if (data.sync_in_progress) {
      statusHtml = '<span class="text-yellow-600 flex items-center"><svg class="w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Syncing...</span>'
      cardClass = 'syncing'
    } else if (data.last_sync_error) {
      statusHtml = `<span class="text-red-600" title="${data.last_sync_error}">Failed</span>`
      cardClass = 'failed'
    } else if (data.dns_synced && !data.dns_sync_to_do) {
      statusHtml = '<span class="text-green-600">Synced</span>'
      cardClass = 'synced'
    } else if (data.dns_sync_to_do) {
      statusHtml = '<span class="text-amber-600">Pending</span>'
      cardClass = 'pending'
    } else {
      statusHtml = '<span class="text-gray-600">Not Synced</span>'
      cardClass = 'not-synced'
    }

    this.route53StatusTarget.innerHTML = statusHtml

    // Update the icon based on status
    if (this.hasRoute53CardTarget) {
      const iconContainer = this.route53CardTarget.querySelector('.flex-shrink-0 > div')
      if (iconContainer) {
        if (cardClass === 'synced') {
          iconContainer.className = 'w-10 h-10 bg-green-500 rounded-full flex items-center justify-center'
          iconContainer.innerHTML = `
            <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          `
        } else if (cardClass === 'syncing') {
          iconContainer.className = 'w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center'
          iconContainer.innerHTML = `
            <svg class="w-6 h-6 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          `
        } else if (cardClass === 'failed') {
          iconContainer.className = 'w-10 h-10 bg-red-500 rounded-full flex items-center justify-center'
          iconContainer.innerHTML = `
            <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          `
        } else if (cardClass === 'pending') {
          iconContainer.className = 'w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center'
          iconContainer.innerHTML = `
            <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd" />
            </svg>
          `
        }
      }
    }
  }

  // Show success notification
  showSuccessNotification() {
    this.showToast('Route53 sync completed successfully!', 'success')
  }

  // Show error notification
  showErrorNotification(error) {
    this.showToast(`Sync failed: ${error}`, 'error')
  }

  // Show timeout notification
  showTimeoutNotification() {
    this.showToast('Sync is taking longer than expected. Please refresh the page to check status.', 'warning')
  }

  // Show a toast notification
  showToast(message, type = 'info') {
    const toast = document.createElement('div')
    let bgColor, icon

    switch (type) {
      case 'success':
        bgColor = 'bg-green-500'
        icon = '<path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />'
        break
      case 'error':
        bgColor = 'bg-red-500'
        icon = '<path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />'
        break
      case 'warning':
        bgColor = 'bg-yellow-500'
        icon = '<path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />'
        break
      default:
        bgColor = 'bg-blue-500'
        icon = '<path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />'
    }

    toast.className = `fixed bottom-4 right-4 ${bgColor} text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center max-w-md`
    toast.innerHTML = `
      <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        ${icon}
      </svg>
      <span class="truncate">${message}</span>
    `

    document.body.appendChild(toast)

    // Longer display for errors
    setTimeout(() => {
      toast.remove()
    }, type === 'error' ? 8000 : 5000)
  }

  // Called when sync button is clicked
  triggerSync(event) {
    // Start polling when sync is triggered
    this.startPolling()
  }
}
